import moment from "moment";
import { EncryptStorage } from "encrypt-storage";
import { ksaCountryCode, routesToHideHeaderAndFooter } from "./constants";
import { Trans } from "react-i18next";
import ReactGA from "react-ga";
import { logoutUser } from "../redux/reducers/userReducer";
import { removeAllItems } from "../redux/reducers/cartReducer";
import axios from "axios";
import FileDownload from "js-file-download";
import { NotificationHelper } from "../components/shared";
import { persistor } from "../redux/store";

// Encryption of data stored in browser storage
export const encryptStorage = (storageType) => {
  const config = {
    prefix: "@ah-ksa",
    storageType: storageType || "localStorage"
  };

  try {
    return new EncryptStorage("secret-key-value", config);
  } catch (error) {
    console.error("Failed to initialize EncryptStorage:", error);
    return null;
  }
};

export const dispatchStorage = (key, data, storageType) => {
  encryptStorage(storageType).setItem(key, data);
};

export const loadStorage = (key, storageType) => {
  try {
    const encryptedStorage = encryptStorage(storageType);
    const encryptedItem = encryptedStorage.getItem(key);

    if (encryptedItem) {
      return encryptedItem;
    }

    // Check if localStorage is accessible
    const localItem = localStorage.getItem(key);
    return localItem ? JSON.parse(localItem) : null;
  } catch (error) {
    console.error("Error accessing storage:", error);
    return null;
  }
};

export const deleteStorage = (key, storageType) => encryptStorage(storageType).removeItem(key);

export const userLogout = async (dispatch) => {
  dispatch(logoutUser());
  dispatch(removeAllItems());
  await persistor.flush();

  window.location.reload();
};

export const scrollPageTop = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth" // for smoothly scrolling
  });
};

export const scrollToSection = (id, gap) => {
  var getMeTo = document.getElementById(id);
  if (getMeTo) {
    const yOffset = gap;
    const y = getMeTo.getBoundingClientRect().top + window.pageYOffset + yOffset;
    // getMeTo.scrollIntoView({ behavior: "smooth" }, false);
    window.scrollTo({ top: y, behavior: "smooth" });
  }
};

export const camelize = (str) => {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, function (word, index) {
      return index === 0 ? word.toLowerCase() : word.toUpperCase();
    })
    .replace(/\s+/g, "");
};

export const isLtr = () => {
  const currentLang = localStorage.getItem("i18nextLng");
  return currentLang === "en" || currentLang === "en-US" || currentLang === "en-GB";
};

export const currentLanguage = () => (isLtr() ? "en" : "ar");

export const getFormatedDate = (date, format) => {
  return date && format ? moment(new Date(date)).format(format) : "";
};

export const getLocalizedDate = (
  date,
  options = { year: "numeric", month: "short", day: "numeric" }
) => {
  const parsedDate = date ? new Date(date) : null;
  if (!parsedDate || isNaN(parsedDate)) return "";

  const locale = currentLanguage() === "ar" ? "ar-SA-u-ca-gregory-nu-latn" : "en-US";
  return new Intl.DateTimeFormat(locale, options).format(parsedDate);
};

// Automatically focus on OTP fields to back and forth
export const automateOTPFields = (e, targetDigit, setShowError) => {
  const val = e.target.value;
  // When moving forwards
  if (setShowError) {
    setShowError(false);

    if (!val.length) return;
    e.target.value = Math.max(0, parseInt(val)).toString().slice(0, 1);
    val && targetDigit?.current.focus();
  }
  // When going backwards
  else {
    if ((e.keyCode || e.charCode) == 8) {
      !val.length && targetDigit.current.focus();
    }
  }
};

// Select input text
export const selectInputText = (e) => e.target.select();

// Car brand and model: Other car select condition
export const isOtherCar = (brand, model) => {
  if (brand === "OTHER" || brand === "سيارة أخرى" || model === "OTHER" || model === "سيارة أخرى")
    return true;
};

// Car plate number: Don't allow alphabets and max 4
export const handleCarPlateNum = (e) => {
  if (!e.target.value) return;
  if (e.target.value.length >= 4) document.getElementById("plateChar").focus();

  e.target.value = e.target.value.slice(0, 4).replace(/[^0-9]/g, "");
};

// Car plate characters: Don't allow numbers and max 3
export const handleCarPlateChar = (e) => {
  if (!e.target.value) return;

  let inputValue = e.target.value;
  inputValue = inputValue
    .replace(/[^A-Za-z\u0600-\u06FF\u0750-\u077F]/g, "")
    .split("")
    .join(" ")
    .slice(0, 5)
    .toUpperCase();
  return (e.target.value = inputValue);
};

// Checking for file type object
export const isFile = (file) => file instanceof File;

// Conditon for (1280px - 1025px) and rest screen sizes
export const is150Res = () => window.innerWidth <= 1280 && window.innerWidth > 1024;

// Checking for Before 2017 key
export const isBefore2017 = (val) =>
  val === "Before 2017" ? <Trans i18nKey="myAccount.before" /> : val;

// Creating car nick name
export const createCarNickName = (car) => {
  return car
    ? car.carNickName?.trim() || car.username + " " + (car.otherModel || car.model?.en || car.model)
    : "";
};

// Analytics
export const initGA = () => {
  ReactGA.initialize("YOUR_TRACKING_ID"); // Replace 'YOUR_TRACKING_ID' with the actual tracking ID provided by Google Analytics
};

export const logPageView = (location) => {
  ReactGA.set({ page: location.pathname }); // user information here
  ReactGA.pageview(location.pathname + location.search);
};

export const logEvent = (category, action, label) => {
  // Add Google Analytics event tracking code here
  // gtag is loaded on live domain only (see public/index.html)
  if (typeof window.gtag === "function") {
    window.gtag("event", action, {
      event_category: category,
      event_label: label
    });
  }

  console.log(category, action, label);
};

export const navigationWithEvents = (key, label, to) => {
  logEvent(key, "button click", label);
  if (to) to();
};

export const logException = (description, fatal = false) => {
  ReactGA.exception({ description, fatal });
};

export const downloadPdf = async (pdf, deposit, refund) => {
  const blob = new Blob([pdf], { type: "application/pdf" });

  // Create a download link for the file
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = refund
    ? `Credit-Memo.pdf`
    : deposit
      ? `Deposit-Receipt.pdf`
      : `Final-Invoice.pdf`;
  document.body.appendChild(link);
  link.click();

  // Clean up the object URL after the download is complete
  URL.revokeObjectURL(url);
};

// Cut out text and add (...)
export const truncateText = (string = "", maxLength = 50) =>
  string.length > maxLength ? `${string.substring(0, maxLength)}…` : string;

// Checks file's extension
export const checkFileExtension = (fileName) => {
  const extension = fileName && fileName.split(".").pop().toLowerCase();
  return extension;
};

// Get search query data from URL
export const getQueryData = (key) => new URL(window.location.href).searchParams.get(key);

// Number input restriction to specified length
export const limitizeValue = (e, limit = 9) => {
  if (e.target.value.length === 0) return;
  e.target.value = Number(Math.abs(e.target.value.slice(0, limit)));
};

// Download sever side files
export const downloadFile = async (key) => {
  if (key === undefined) return;
  let originalKey = key;
  // let filename = originalKey;
  // filename = key.slice(37, key.length);
  let filename = originalKey?.split("2442")?.[1] || key.slice(37, key.length);
  const response = await axios.get("/api/util/download", {
    params: { key },
    headers: { imageKey: "xzic2b7Fei1GJlKuQsJcBtIo12qfnJ5W" },
    progressBar: true,
    responseType: "blob"
  });

  let data = [];
  data = response.config.params.key.split("75973");
  if (!data || !data.length) data = response.config.params.key.split("43770");
  filename = filename != undefined && filename != null && filename != "" ? filename : data[1];
  FileDownload(response.data, filename, "application/pdf image/jpeg image/jpg image/png");
};

// Create city options
export const getCitiesFromBranches = (branches) => {
  let cities = branches.map((sp) => ({ en: sp.city, ar: sp.cityNameAr }));
  cities = Object.values(
    cities.reduce((acc, item) => {
      acc[item.en] = acc[item.en] || item;
      return acc;
    }, {})
  );
  return cities;
};

// Get phone number without KSA country code
export const removeKSACountryCode = (phoneNumber) =>
  Number(phoneNumber?.replace(ksaCountryCode, ""));

// Get phone number without KSA country code
export const concatOTPDigits = (digits) =>
  digits.code1.toString() +
  digits.code2.toString() +
  digits.code3.toString() +
  digits.code4.toString();

// Check and notify for internet connection
export const internetDetector = () => {
  window.addEventListener("online", () =>
    NotificationHelper.success(<Trans i18nKey="myAccount.online" />)
  );
  window.addEventListener("offline", () =>
    NotificationHelper.error(<Trans i18nKey="myAccount.offline" />)
  );
};

// Email format validator
export const emailFormatValidator = (value) => {
  if (!value) return true;
  return /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(value);
};

// Remove extra trailing slashes from pathname
export const getLocationPathname = () => window.location.pathname.replace(/\/+$/, "");

export const openPdfFile = (file) => {
  if (!file) return;
  const pdfUrl = URL.createObjectURL(file);
  window.open(pdfUrl, "_blank");
  setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
};

// Check if the file is PDF
export const isPdfFile = (file) => file && file?.type === "application/pdf";

// Fomat price with commas
export const formatAmount = (amount, forceDecimals = false) => {
  const num = Number(amount);
  return isNaN(num)
    ? "0"
    : num.toLocaleString(
        undefined,
        num % 1 || forceDecimals ? { minimumFractionDigits: 2, maximumFractionDigits: 2 } : {}
      );
};

// Generate random unique id
export const generateUniqueId = () => {
  const array = new Uint8Array(16);
  window.crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array));
};

// Convert string to sentence case
export const convertToSentenceCase = (text) =>
  text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();

export const shouldHideHeaderFooter = () =>
  routesToHideHeaderAndFooter.some((route) =>
    route.includes("/:id")
      ? window.location.pathname.startsWith(route.replace("/:id", ""))
      : window.location.pathname === route
  );
